import TextField from '../../../shared/components/TextField/TextField';
import CardContent from '../../../shared/components/CardContent/CardContent';
import CircularProgress from '../../../shared/components/CircularProgress/CircularProgress';
import Divider from '../../../shared/components/Divider/Divider';
import Grid from '../../../shared/components/Grid/Grid';
import LinearProgress from '../../../shared/components/LinearProgress/LinearProgress';
import Paper from '../../../shared/components/Paper/Paper';
import Stack from '../../../shared/components/Stack/Stack';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import Alert from '../../../shared/components/Alert/Alert';
import Box from '../../../shared/components/Box/Box';
import Button from '../../../shared/components/Button/Button';
import Card from '../../../shared/components/Card/Card';
import Chip from '../../../shared/components/Chip/Chip';
import Typography from '../../../shared/components/Typography/Typography';
import AddTaskIcon from '@mui/icons-material/AddTask';
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import FlagOutlinedIcon from '@mui/icons-material/FlagOutlined';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import {
  completeTask,
  createTask,
  getTaskAssignableUsers,
  getUserTasks,
  getTimeTrackableTasks,
  updateTask,
} from './tasks.api';

const initialFormState = {
  title: '',
  description: '',
  status: 'todo',
  priority: 'medium',
  taskType: 'normal',
  estimatedHours: '',
  startDate: '',
  dueDate: '',
  assignedUserId: '',
  timeTrackingEnabled: true,
};

const statusLabels = {
  todo: 'À faire',
  pending: 'À faire',
  in_progress: 'En cours',
  completed: 'Terminée',
  done: 'Terminée',
  blocked: 'Bloquée',
};

const priorityLabels = {
  low: 'Basse',
  medium: 'Moyenne',
  high: 'Haute',
  critical: 'Urgente',
};

const priorityColors = {
  low: 'default',
  medium: 'info',
  high: 'warning',
  critical: 'error',
};

const getCurrentUser = () => {
  try {
    return JSON.parse(localStorage.getItem('currentUser') || '{}');
  } catch {
    return {};
  }
};

const formatDate = (value) => {
  if (!value) return 'Non définie';
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? value
    : date.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' });
};

const toDateInputValue = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value).slice(0, 10);
  return date.toISOString().slice(0, 10);
};

function TasksPage() {
  const formRef = useRef(null);
  const [tasks, setTasks] = useState([]);
  const [recentCompletedTasks, setRecentCompletedTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [completingId, setCompletingId] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(initialFormState);
  const [assignableUsers, setAssignableUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const currentUser = useMemo(getCurrentUser, []);
  const userId = currentUser?.id;
  const isAdmin = Number(currentUser?.roleId || currentUser?.role_id) === 1
    || String(currentUser?.role || '').toLowerCase() === 'admin';
  const isManager = Number(currentUser?.roleId || currentUser?.role_id) === 3
    || String(currentUser?.role || '').toLowerCase() === 'manager';
  const canAssignTasks = isAdmin || isManager;

  const resetForm = useCallback(() => {
    setForm({
      ...initialFormState,
      assignedUserId: canAssignTasks && userId ? String(userId) : '',
    });
  }, [canAssignTasks, userId]);

  const fetchTasks = useCallback(async () => {
    if (!userId) {
      setTasks([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const [activeData, trackableData] = await Promise.all([
        getUserTasks(userId),
        getTimeTrackableTasks(30),
      ]);
      const activeTasks = Array.isArray(activeData) ? activeData : activeData.tasks || activeData.data || [];
      const trackableTasks = Array.isArray(trackableData) ? trackableData : trackableData.tasks || trackableData.data || [];
      setTasks(activeTasks);
      setRecentCompletedTasks(trackableTasks.filter((task) => ['done', 'completed'].includes(String(task.status || '').toLowerCase())));
      setError(null);
    } catch (err) {
      setTasks([]);
      setError(err.message || 'Erreur lors du chargement des tâches.');
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    fetchTasks();
  }, [fetchTasks]);

  useEffect(() => {
    if (!canAssignTasks) return;

    const fetchUsers = async () => {
      try {
        setUsersLoading(true);
        const data = await getTaskAssignableUsers();
        const users = (Array.isArray(data) ? data : data.data || [])
          .filter((user) => user.isActive !== false)
          .sort((first, second) =>
            `${first.lastName || ''} ${first.firstName || ''}`.localeCompare(
              `${second.lastName || ''} ${second.firstName || ''}`,
              'fr'
            ));
        setAssignableUsers(users);
        setForm((current) => ({
          ...current,
          assignedUserId: current.assignedUserId || String(userId),
        }));
      } catch (err) {
        setError(err.message || 'Impossible de charger la liste des utilisateurs.');
      } finally {
        setUsersLoading(false);
      }
    };

    fetchUsers();
  }, [canAssignTasks, userId]);

  const handleChange = (event) => {
    const { name, value, type, checked } = event.target;
    if (name === 'taskType') {
      setForm((current) => ({ ...current, taskType: value, timeTrackingEnabled: value !== 'personal' }));
      return;
    }
    setForm((current) => ({ ...current, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError(null);
    setSuccess(null);

    try {
      setSubmitting(true);
      const payload = {
        ...form,
        assignedUserId: canAssignTasks ? Number(form.assignedUserId) : userId,
        estimatedHours: form.estimatedHours === '' ? null : Number(form.estimatedHours),
        timeTrackingEnabled: Boolean(form.timeTrackingEnabled),
      };

      if (editingId) {
        const updatedTask = await updateTask(editingId, payload);
        setTasks((current) => Number(updatedTask.assignedUserId) === Number(userId)
          ? current.map((task) => (task.id === editingId ? updatedTask : task))
          : current.filter((task) => task.id !== editingId));
        setSuccess('La tâche a bien été modifiée.');
      } else {
        const newTask = await createTask({
          ...payload,
          remainingHours: payload.estimatedHours,
          progressPercent: 0,
        });
        if (Number(newTask.assignedUserId) === Number(userId)) {
          setTasks((current) => [newTask, ...current]);
        }
        setSuccess('La tâche a bien été créée.');
      }

      resetForm();
      setEditingId(null);
    } catch (err) {
      setError(err.message || `Erreur lors de ${editingId ? 'la modification' : 'la création'} de la tâche.`);
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (task) => {
    setEditingId(task.id);
    setForm({
      title: task.title || '',
      description: task.description || '',
      status: task.status || 'todo',
      priority: task.priority || 'medium',
      taskType: task.taskType || task.task_type || 'normal',
      estimatedHours: task.estimatedHours ?? task.estimated_hours ?? '',
      startDate: toDateInputValue(task.startDate || task.start_date),
      dueDate: toDateInputValue(task.dueDate || task.due_date),
      assignedUserId: String(task.assignedUserId || task.assigned_user_id || userId),
      timeTrackingEnabled: task.timeTrackingEnabled ?? task.time_tracking_enabled ?? (task.taskType !== 'personal'),
    });
    setError(null);
    setSuccess(null);
    window.requestAnimationFrame(() => {
      if (window.matchMedia('(max-width: 1199px)').matches) {
        formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    });
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    resetForm();
  };

  const handleComplete = async (taskId) => {
    setError(null);
    setSuccess(null);

    try {
      setCompletingId(taskId);
      const completed = await completeTask(taskId);
      setTasks((current) => current.filter((task) => task.id !== taskId));
      if (completed && (completed.timeTrackingEnabled ?? completed.time_tracking_enabled)) {
        setRecentCompletedTasks((current) => [completed, ...current.filter((task) => task.id !== taskId)]);
      }
      setSuccess('La tâche a été marquée comme terminée. Le pointage reste disponible si le suivi du temps est activé.');
    } catch (err) {
      setError(err.message || 'Impossible de terminer cette tâche.');
    } finally {
      setCompletingId(null);
    }
  };

  const inProgressCount = tasks.filter((task) => task.status === 'in_progress').length;
  const urgentCount = tasks.filter((task) => ['high', 'critical'].includes(task.priority)).length;

  return (
    <Box sx={{ width: '100%', maxWidth: 1480, mx: 'auto', p: { xs: 2, md: 3 } }}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        spacing={2}
        sx={{ mb: 3 }}
      >
        <Box>
          <Typography variant="overline" color="primary" fontWeight={700}>
            Organisation personnelle
          </Typography>
          <Typography variant="h4" component="h1" fontWeight={750}>
            Mes tâches
          </Typography>
          <Typography color="text.secondary" sx={{ mt: 0.5 }}>
            Consultez vos priorités et créez de nouvelles tâches.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          <Chip icon={<TaskAltIcon />} label={`${tasks.length} en attente`} variant="outlined" />
          <Chip label={`${inProgressCount} en cours`} color="info" variant="outlined" />
          <Chip label={`${urgentCount} prioritaires`} color={urgentCount ? 'warning' : 'default'} variant="outlined" />
        </Stack>
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>{success}</Alert>}
      {!userId && <Alert severity="warning" sx={{ mb: 2 }}>Connectez-vous pour consulter et créer vos tâches.</Alert>}

      <Grid container spacing={3} alignItems="flex-start">
        <Grid size={{ xs: 12, lg: 8 }}>
          <Paper variant="outlined" sx={{ p: { xs: 2, md: 3 }, borderRadius: 3 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
              <Box>
                <Typography variant="h6" fontWeight={700}>Tâches à traiter</Typography>
                <Typography variant="body2" color="text.secondary">
                  Classées de la plus récente à la plus ancienne
                </Typography>
              </Box>
            </Stack>

            {loading ? (
              <Stack alignItems="center" spacing={2} sx={{ py: 8 }}>
                <CircularProgress size={32} />
                <Typography color="text.secondary">Chargement des tâches…</Typography>
              </Stack>
            ) : tasks.length === 0 ? (
              <Stack alignItems="center" textAlign="center" spacing={1} sx={{ py: 8, px: 2 }}>
                <CheckCircleOutlineIcon color="success" sx={{ fontSize: 52 }} />
                <Typography variant="h6">Tout est à jour</Typography>
                <Typography color="text.secondary">
                  Vous n’avez aucune tâche en attente pour le moment.
                </Typography>
              </Stack>
            ) : (
              <Stack spacing={2}>
                {tasks.map((task) => {
                  const taskId = task.id || task._id;
                  const progress = Number(task.progressPercent || 0);
                  return (
                    <Card key={taskId || task.title} padding="none" variant="outlined" sx={{ borderRadius: 2.5 }}>
                      <CardContent sx={{ '&:last-child': { pb: 2.5 } }}>
                        <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={2}>
                          <Box sx={{ minWidth: 0, flex: 1 }}>
                            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
                              <Chip size="small" label={statusLabels[task.status] || task.status || 'À faire'} color={task.status === 'in_progress' ? 'info' : 'default'} />
                              <Chip size="small" icon={<FlagOutlinedIcon />} label={priorityLabels[task.priority] || task.priority || 'Non définie'} color={priorityColors[task.priority] || 'default'} variant="outlined" />
                              {task.taskType === 'personal' && <Chip size="small" label="Personnelle" variant="outlined" />}
                              <Chip size="small" label={(task.timeTrackingEnabled ?? task.time_tracking_enabled ?? task.taskType !== 'personal') ? 'Temps suivi' : 'Sans pointage'} variant="outlined" />
                            </Stack>
                            <Typography variant="h6" fontWeight={700}>{task.title || 'Sans titre'}</Typography>
                            {task.description && (
                              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75, whiteSpace: 'pre-wrap' }}>
                                {task.description}
                              </Typography>
                            )}
                          </Box>
                          <Stack
                            direction={{ xs: 'row', sm: 'column' }}
                            spacing={1}
                            sx={{ alignSelf: { xs: 'stretch', sm: 'flex-start' } }}
                          >
                            <Button
                              variant={editingId === taskId ? 'contained' : 'outlined'}
                              size="small"
                              startIcon={<EditOutlinedIcon />}
                              onClick={() => handleEdit(task)}
                              disabled={submitting}
                              sx={{ whiteSpace: 'nowrap' }}
                            >
                              Modifier
                            </Button>
                            <Button
                              variant="outlined"
                              color="success"
                              size="small"
                              startIcon={completingId === taskId ? <CircularProgress size={16} color="inherit" /> : <CheckCircleOutlineIcon />}
                              onClick={() => handleComplete(taskId)}
                              disabled={completingId === taskId}
                              sx={{ whiteSpace: 'nowrap' }}
                            >
                              Terminer
                            </Button>
                          </Stack>
                        </Stack>
                        <Divider sx={{ my: 2 }} />
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 1, sm: 3 }}>
                          <Stack direction="row" spacing={1} alignItems="center">
                            <CalendarTodayOutlinedIcon sx={{ fontSize: 17, color: 'text.secondary' }} />
                            <Typography variant="body2" color="text.secondary">Échéance : {formatDate(task.dueDate || task.due_date)}</Typography>
                          </Stack>
                          <Typography variant="body2" color="text.secondary">
                            {task.estimatedHours ?? task.estimated_hours ?? 0} h estimées
                          </Typography>
                          {taskId && (task.timeTrackingEnabled ?? task.time_tracking_enabled ?? task.taskType !== 'personal') && (
                            <Button component={Link} to={`/temps?taskId=${taskId}`} size="small" variant="outlined">
                              Pointer du temps
                            </Button>
                          )}
                        </Stack>
                        {progress > 0 && (
                          <Box sx={{ mt: 2 }}>
                            <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
                              <Typography variant="caption" color="text.secondary">Progression</Typography>
                              <Typography variant="caption" fontWeight={700}>{progress}%</Typography>
                            </Stack>
                            <LinearProgress variant="determinate" value={Math.min(progress, 100)} sx={{ height: 6, borderRadius: 99 }} />
                          </Box>
                        )}
                      </CardContent>
                    </Card>
                  );
                })}
              </Stack>
            )}
          </Paper>

          {recentCompletedTasks.length > 0 && (
            <Paper variant="outlined" sx={{ p: { xs: 2, md: 3 }, borderRadius: 3, mt: 3 }}>
              <Typography variant="h6" fontWeight={700}>Terminées récemment</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Ces tâches restent pointables pendant 30 jours après leur clôture.
              </Typography>
              <Stack spacing={1.25}>
                {recentCompletedTasks.map((task) => {
                  const taskId = task.id || task._id;
                  return (
                    <Card key={taskId} padding="none" variant="outlined" sx={{ borderRadius: 2 }}>
                      <CardContent sx={{ '&:last-child': { pb: 2 } }}>
                        <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={1.5}>
                          <Box>
                            <Typography fontWeight={700}>{task.title || 'Sans titre'}</Typography>
                            <Typography variant="body2" color="text.secondary">Terminée · pointage encore disponible</Typography>
                          </Box>
                          <Button component={Link} to={`/temps?taskId=${taskId}`} size="small" variant="outlined">
                            Pointer du temps
                          </Button>
                        </Stack>
                      </CardContent>
                    </Card>
                  );
                })}
              </Stack>
            </Paper>
          )}
        </Grid>

        <Grid size={{ xs: 12, lg: 4 }}>
          <Paper
            ref={formRef}
            component="form"
            onSubmit={handleSubmit}
            variant="outlined"
            sx={{ p: { xs: 2, md: 3 }, borderRadius: 3, position: { lg: 'sticky' }, top: { lg: 88 } }}
          >
            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 0.5 }}>
              {editingId ? <EditOutlinedIcon color="primary" /> : <AddTaskIcon color="primary" />}
              <Typography variant="h6" fontWeight={700}>
                {editingId ? 'Modifier la tâche' : 'Nouvelle tâche'}
              </Typography>
            </Stack>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
              {editingId
                ? 'Mettez à jour les informations puis enregistrez vos changements.'
                : 'Ajoutez rapidement une action à votre liste.'}
            </Typography>

            <Stack spacing={2}>
              <TextField label="Titre" name="title" value={form.title} onChange={handleChange} required fullWidth disabled={!userId} />
              <TextField label="Description" name="description" value={form.description} onChange={handleChange} multiline minRows={3} fullWidth disabled={!userId} />
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 6, lg: 12, xl: 6 }}>
                  <TextField select label="Statut" name="status" value={form.status} onChange={handleChange} fullWidth disabled={!userId}>
                    <option value="todo">À faire</option>
                    <option value="in_progress">En cours</option>
                    <option value="blocked">Bloquée</option>
                  </TextField>
                </Grid>
                <Grid size={{ xs: 12, sm: 6, lg: 12, xl: 6 }}>
                  <TextField select label="Priorité" name="priority" value={form.priority} onChange={handleChange} fullWidth disabled={!userId}>
                    <option value="low">Basse</option>
                    <option value="medium">Moyenne</option>
                    <option value="high">Haute</option>
                    <option value="critical">Urgente</option>
                  </TextField>
                </Grid>
              </Grid>
              {canAssignTasks && (
                <TextField
                  select
                  required
                  label="Attribuer à"
                  name="assignedUserId"
                  value={form.assignedUserId}
                  onChange={handleChange}
                  fullWidth
                  disabled={!userId || usersLoading}
                  helperText={usersLoading ? 'Chargement des utilisateurs…' : 'Choisissez le responsable de cette tâche.'}
                >
                  {assignableUsers.map((user) => (
                    <option key={user.id} value={String(user.id)}>
                      {[user.firstName, user.lastName].filter(Boolean).join(' ') || user.email}
                      {Number(user.id) === Number(userId) ? ' (moi)' : ''}
                    </option>
                  ))}
                </TextField>
              )}
              <TextField select label="Type de tâche" name="taskType" value={form.taskType} onChange={handleChange} fullWidth disabled={!userId}>
                <option value="normal">Normale</option>
                <option value="personal">Personnelle</option>
              </TextField>
              <Box component="label" sx={{ display: 'flex', gap: 1.25, alignItems: 'flex-start', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  name="timeTrackingEnabled"
                  checked={Boolean(form.timeTrackingEnabled)}
                  onChange={handleChange}
                  disabled={!userId}
                  style={{ marginTop: 4 }}
                />
                <Box>
                  <Typography variant="body2" fontWeight={700}>Suivre du temps sur cette tâche</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Désactivez cette option pour un rappel ou une tâche d'organisation qui ne nécessite aucun pointage.
                  </Typography>
                </Box>
              </Box>
              <TextField label="Heures estimées" name="estimatedHours" type="number" value={form.estimatedHours} onChange={handleChange} inputProps={{ min: 0, step: 0.5 }} fullWidth disabled={!userId} />
              <TextField
                label="Date de début"
                name="startDate"
                type="date"
                value={form.startDate}
                onChange={handleChange}
                slotProps={{ inputLabel: { shrink: true } }}
                fullWidth
                disabled={!userId}
              />
              <TextField
                label="Échéance"
                name="dueDate"
                type="date"
                value={form.dueDate}
                onChange={handleChange}
                slotProps={{ inputLabel: { shrink: true } }}
                fullWidth
                disabled={!userId}
              />
              <Stack direction={{ xs: 'column', sm: 'row', lg: 'column', xl: 'row' }} spacing={1}>
                <Button
                  type="submit"
                  variant="contained"
                  size="large"
                  fullWidth
                  startIcon={submitting
                    ? <CircularProgress size={18} color="inherit" />
                    : editingId ? <EditOutlinedIcon /> : null}
                  disabled={!userId || submitting}
                >
                  {submitting
                    ? (editingId ? 'Enregistrement…' : 'Création…')
                    : (editingId ? 'Enregistrer' : 'Créer la tâche')}
                </Button>
                {editingId && (
                  <Button
                    type="button"
                    variant="outlined"
                    size="large"
                    onClick={handleCancelEdit}
                    disabled={submitting}
                  >
                    Annuler
                  </Button>
                )}
              </Stack>
            </Stack>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
}

export default TasksPage;
